/**
 * List-scoped refresh for the room list.
 *
 * GRMS specification §11.1, "Cached values and refresh" and the REST
 * revalidation sequence: "A client MUST subscribe before its bootstrap/refetch,
 * invalidate an in-flight snapshot when a relevant notification arrives, and
 * refetch after that snapshot completes; it MUST NOT overwrite newer
 * observations with an older response."
 *
 * The room list is one REST snapshot per site, computed server-side from a
 * single database read. Everything a card shows — `lightsOn`, `temperature`,
 * `hvacSetpoint`, `windowOpen`, `lockOpen`, `lockLocked`, `hvacMode`, `locks` —
 * comes from that one response. So refresh belongs to the **list**, not to the
 * card: a card that fetched for itself would produce one request per room, and
 * a hundred rooms would produce a hundred requests that each recompute the same
 * snapshot.
 *
 * This hook owns the three refresh rules that are easy to get subtly wrong:
 *
 * 1. **A notification during a fetch does not cancel it — it marks it stale.**
 *    The in-flight snapshot was computed before the change, so it is discarded
 *    *after* it lands and a fresh one is requested. Cancelling instead would
 *    leave the list showing the previous snapshot with no read in flight.
 * 2. **An old site's response is never applied.** Site switching rekeys the
 *    query, and a late response for the previous site cannot reach the new key.
 * 3. **Configuration is revalidated within 30 seconds while the list is
 *    visible.** Room params, template schemas and mode labels change through
 *    configuration edits that publish no runtime notification.
 *
 * What it deliberately does not do: it never treats a successful command or a
 * fresh REST response as proof that a device is reachable or that its sample is
 * current. A retained value stays "last reported"; the passage of time alone
 * replaces nothing with a default.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore.js';
import { queryKeys } from './useApi.js';
/**
 * The contract's upper bound on how stale a visible list's configuration-derived
 * fields may be. Runtime values arrive by notification; this covers the ones
 * that do not.
 */
export const VISIBLE_LIST_CONFIGURATION_REFRESH_MS = 30_000;
/**
 * Wire the room list's refresh lifecycle.
 *
 * Call it once where the list lives, and hand `invalidate` to the live
 * subscription. Subscribe **before** bootstrapping the list, so a change
 * published between the subscription and the response is not lost.
 */
export function useRoomListRefresh(options) {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    const enabled = options?.enabled ?? true;
    const isFetching = options?.isFetching ?? false;
    const connectionState = options?.connectionState;
    const pendingInvalidation = useRef(false);
    const previousConnectionState = useRef(connectionState);
    const isFetchingRef = useRef(isFetching);
    useLayoutEffect(() => {
        isFetchingRef.current = isFetching;
    }, [isFetching]);
    const refetchNow = useCallback(() => {
        if (!siteCode)
            return;
        void queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode), refetchType: 'active' });
    }, [queryClient, siteCode]);
    // Every other UI topic is QoS 0 and not retained, so a session that was down
    // or suspended missed the stays, summary and alarm broadcasts too; those
    // re-read with the list (GRMS §12.1 "A reconnecting client MUST bootstrap").
    // `['alarms', site]` covers the active list and every room's alarms.
    const resyncSnapshots = useCallback(() => {
        if (!siteCode)
            return;
        for (const queryKey of [
            queryKeys.todayStays(siteCode),
            queryKeys.occupancySummary(siteCode),
            queryKeys.housekeepingSummary(siteCode),
            queryKeys.activeAlarms(siteCode),
        ]) {
            void queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
        }
    }, [queryClient, siteCode]);
    const invalidate = useCallback(() => {
        if (!siteCode)
            return;
        if (isFetchingRef.current) {
            // The read in flight was computed before this change. Remember to
            // discard it rather than cancelling: cancelling would leave the list on
            // the previous snapshot with nothing on the way.
            pendingInvalidation.current = true;
            return;
        }
        refetchNow();
    }, [refetchNow, siteCode]);
    // The deferred refetch, run when the stale snapshot has landed.
    useEffect(() => {
        if (isFetching || !pendingInvalidation.current)
            return;
        pendingInvalidation.current = false;
        refetchNow();
    }, [isFetching, refetchNow]);
    // Site switch: the previous site's cached list is dropped so no card can
    // render another site's rooms while the new snapshot is on its way. The
    // response for the old site cannot reach the new key either way.
    useEffect(() => {
        pendingInvalidation.current = false;
        return () => {
            queryClient.removeQueries({ queryKey: queryKeys.rooms(siteCode || ''), exact: true });
        };
    }, [queryClient, siteCode]);
    // Configuration-derived fields, on the visible-list bound.
    useEffect(() => {
        if (!enabled || !siteCode)
            return;
        const timer = setInterval(refetchNow, VISIBLE_LIST_CONFIGURATION_REFRESH_MS);
        return () => clearInterval(timer);
    }, [enabled, refetchNow, siteCode]);
    // Browser `online` is not the same thing as the MQTT session reconnecting.
    // Revalidate after an actual live-session recovery as well.
    useEffect(() => {
        const previous = previousConnectionState.current;
        previousConnectionState.current = connectionState;
        if (enabled && connectionState === 'connected' && previous && previous !== 'connected') {
            invalidate();
            resyncSnapshots();
        }
    }, [connectionState, enabled, invalidate, resyncSnapshots]);
    // Reconnection and foreground resume. A resumed tab may have missed every
    // notification while it was suspended, so it re-reads rather than trusting
    // whatever it was showing.
    useEffect(() => {
        if (!enabled || !siteCode || typeof window === 'undefined')
            return;
        const onOnline = () => {
            refetchNow();
            resyncSnapshots();
        };
        const onResume = () => {
            if (document.visibilityState === 'visible')
                onOnline();
        };
        window.addEventListener('online', onOnline);
        document.addEventListener('visibilitychange', onResume);
        return () => {
            window.removeEventListener('online', onOnline);
            document.removeEventListener('visibilitychange', onResume);
        };
    }, [enabled, refetchNow, resyncSnapshots, siteCode]);
    return {
        siteCode: siteCode ?? null,
        invalidate,
    };
}
//# sourceMappingURL=useRoomListRefresh.js.map